import type { StageInvalidationReason } from '@kyc/shared';

/**
 * §25 — app backgrounded, orientation change or camera interruption invalidates the
 * current stage take. The watcher reports the reason; the flow controller aborts the
 * take and (when a server-visible stage exists) posts the invalidation event.
 */
export class InterruptionWatcher {
  private handlers: Array<() => void> = [];

  constructor(private onInterrupt: (reason: StageInvalidationReason) => void) {}

  watch(track: MediaStreamTrack): void {
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') this.onInterrupt('app_backgrounded');
    };
    const onOrientation = () => this.onInterrupt('orientation_changed');
    const onTrackEnded = () => this.onInterrupt('camera_interrupted');
    const onTrackMute = () => this.onInterrupt('camera_interrupted');

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('orientationchange', onOrientation);
    track.addEventListener('ended', onTrackEnded);
    track.addEventListener('mute', onTrackMute);

    this.handlers.push(() => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('orientationchange', onOrientation);
      track.removeEventListener('ended', onTrackEnded);
      track.removeEventListener('mute', onTrackMute);
    });
  }

  dispose(): void {
    for (const h of this.handlers) h();
    this.handlers = [];
  }
}
